import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { EntitySelect, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import { GetTeamsForSelectorDocument } from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import { EmployeeSelectorV2MultiSelectState } from 'modules/employees/components/EmployeeSelectorV2/types'

export interface AffecteeEntry {
  criteria?: string | null
  criteriaInput?: { teamId?: number | null } | null
  employeeIds?: number[] | null
}

type AffecteeType = 'anyone_in_team' | 'leads_in_team' | 'specific_employees'

interface Props {
  value: AffecteeEntry[]
  onChange: (entries: AffecteeEntry[]) => void
}

const AFFECTEE_TYPE_OPTIONS: { value: AffecteeType; label: string }[] = [
  { value: 'anyone_in_team', label: 'Team members' },
  { value: 'leads_in_team', label: 'Team leads' },
  { value: 'specific_employees', label: 'Specific employees' },
]

const TeamSelectorField = ({
  value,
  onChange,
}: {
  value: number | null
  onChange: (teamId: number | null) => void
}) => {
  const { data, loading } = useQuery(GetTeamsForSelectorDocument)
  const availableTeams = data?.teams?.teamsConnection?.nodes || []
  const selectedTeam = value ? availableTeams.find((team) => team.id === value) : undefined
  const selectedEntities = selectedTeam
    ? [{ id: selectedTeam.id, name: selectedTeam.name, avatar: buildUrl(selectedTeam.avatar) }]
    : []

  return (
    <EntitySelect
      label="Select team"
      hideLabel
      loading={loading}
      entities={availableTeams.map(({ id, avatar, name }) => ({
        avatar: buildUrl(avatar),
        expanded: false,
        id,
        name,
      }))}
      notFoundSubtitle=""
      notFoundTitle="No teams found"
      onGroupChange={() => {}}
      onItemExpandedChange={() => {}}
      onSelect={(selection: EntitySelectEntity | null) => {
        onChange(selection?.id != null ? Number(selection.id) : null)
      }}
      searchPlaceholder="Search teams"
      selectedEntities={selectedEntities}
      selectedLabel=""
      singleSelector
      size="md"
      placeholder="Select team"
      selectedItemsCopy=""
      groups={[]}
      selectedGroup="all"
      clearLabel="Clear"
      selectAllLabel=""
    />
  )
}

interface AffecteeRowProps {
  type: AffecteeType
  teamId: number | null
  employeeIds: number[]
  onTypeChange: (type: AffecteeType) => void
  onTeamChange: (teamId: number | null) => void
  onEmployeeIdsChange: (ids: number[]) => void
  onRemove: () => void
}

const AffecteeRow = ({
  type,
  teamId,
  employeeIds,
  onTypeChange,
  onTeamChange,
  onEmployeeIdsChange,
  onRemove,
}: AffecteeRowProps) => {
  const handleEmployeeChange = (state: EmployeeSelectorV2MultiSelectState) => {
    if (state.type === 'manual') {
      onEmployeeIdsChange(state.ids)
    }
  }

  return (
    <Box
      flexDirection="column"
      gap="s8"
      padding="s12"
      border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
      borderRadius={{ all: 'abs008' }}
    >
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexEnd" gap="s8">
        <Box flexGrow>
          <SelectField
            label="Criteria"
            value={type}
            onChange={(value: string) => {
              if (
                value === 'anyone_in_team' ||
                value === 'leads_in_team' ||
                value === 'specific_employees'
              ) {
                onTypeChange(value)
              }
            }}
            options={AFFECTEE_TYPE_OPTIONS}
          />
        </Box>
        <Button type="secondary" onClick={onRemove}>
          Remove
        </Button>
      </Box>

      {(type === 'anyone_in_team' || type === 'leads_in_team') && (
        <TeamSelectorField value={teamId} onChange={onTeamChange} />
      )}

      {type === 'specific_employees' && (
        <EmployeeSelectorV2
          multiple
          value={employeeIds}
          onChange={handleEmployeeChange}
          label="Select employees"
          options={{
            hideLabel: true,
            size: 'md',
            disableSelectAll: true,
          }}
          messages={{
            placeholder: 'Search employees...',
            searchBoxPlaceholder: 'Search employees...',
            searchEmptyMessage: 'No employees found',
          }}
        />
      )}
    </Box>
  )
}

interface InternalEntry {
  type: AffecteeType
  teamId: number | null
  employeeIds: number[]
}

const isAffecteeType = (value: string | null | undefined): value is AffecteeType =>
  value === 'anyone_in_team' || value === 'leads_in_team' || value === 'specific_employees'

const entryToInternal = (entry: AffecteeEntry): InternalEntry => {
  if (entry.employeeIds && entry.employeeIds.length > 0) {
    return { type: 'specific_employees', teamId: null, employeeIds: entry.employeeIds }
  }
  return {
    type: isAffecteeType(entry.criteria) ? entry.criteria : 'anyone_in_team',
    teamId: entry.criteriaInput?.teamId ?? null,
    employeeIds: [],
  }
}

const internalToEntry = (internal: InternalEntry): AffecteeEntry | null => {
  if (internal.type === 'specific_employees') {
    if (internal.employeeIds.length === 0) return null
    return { employeeIds: internal.employeeIds }
  }
  if (!internal.teamId) return null
  return {
    criteria: internal.type,
    criteriaInput: { teamId: internal.teamId },
  }
}

const AffecteesSelector = ({ value, onChange }: Props) => {
  const [entries, setEntries] = useState<InternalEntry[]>(() =>
    value.length > 0 ? value.map(entryToInternal) : []
  )

  const syncOnChange = (updated: InternalEntry[]) => {
    setEntries(updated)
    const converted: AffecteeEntry[] = updated
      .map(internalToEntry)
      .filter((e): e is AffecteeEntry => e !== null)
    onChange(converted)
  }

  const handleAdd = () => {
    syncOnChange([...entries, { type: 'anyone_in_team', teamId: null, employeeIds: [] }])
  }

  const handleRemove = (index: number) => {
    syncOnChange(entries.filter((_, i) => i !== index))
  }

  const handleUpdate = (index: number, updated: Partial<InternalEntry>) => {
    const newEntries = entries.map((entry, i) => (i === index ? { ...entry, ...updated } : entry))
    syncOnChange(newEntries)
  }

  return (
    <Box flexDirection="column" gap="s12">
      {entries.map((entry, index) => (
        <AffecteeRow
          key={index}
          type={entry.type}
          teamId={entry.teamId}
          employeeIds={entry.employeeIds}
          onTypeChange={(type) => handleUpdate(index, { type, teamId: null, employeeIds: [] })}
          onTeamChange={(teamId) => handleUpdate(index, { teamId })}
          onEmployeeIdsChange={(employeeIds) => handleUpdate(index, { employeeIds })}
          onRemove={() => handleRemove(index)}
        />
      ))}
      <Button type="secondary" onClick={handleAdd}>
        Add people group
      </Button>
    </Box>
  )
}

export default AffecteesSelector
